import { useState, useEffect, useMemo } from "react";
import { Building } from "lucide-react";
import { useT } from "@/lib/translation";

const INSTITUTION_FOLDER = "CUSTOM";
const INSTITUTION_LOGOS: Record<string, string> = {
  "casa de investimentos": "CasadeInvestimentos",
  bankinter: "Bankinter",
  stoik: "Stoik",
  "golden sgf": "GoldenSGF",
};

// Asset classes issued by a single institution regardless of how the user named the
// holding (e.g. all Certificados de Aforro are issued by CTT/IGCP) — matched on the
// listing's asset_class instead of company_name, which is free text.
const ASSET_CLASS_LOGOS: Record<string, string> = {
  CERTIFICADO_AFORRO: "CTT",
};

const ETF_PROVIDER_FOLDER = "ETF_PROVIDERS";
const ETF_PROVIDER_LOGOS: Record<string, string> = {
  vanguard: "Vanguard",
  spdr: "SPDR",
  invesco: "Invesco",
  ishares: "iShares",
  amundi: "Amundi",
  xtrackers: "Xtrackers",
  schwab: "Schwab",
  jpmorgan: "JPMorgan",
  "first trust": "FirstTrust",
  "goldman sachs": "GoldmanSachs",
  fidelity: "Fidelity",
  vaneck: "VanEck",
  wisdomtree: "WisdomTree",
  dimensional: "Dimensional",
  "global x": "GlobalX",
  "ark invest": "ARK",
  ark: "ARK",
  ubs: "UBS",
  hsbc: "HSBC",
  "legal & general": "LegalGeneral",
  "l&g": "LegalGeneral",
  "bnp paribas": "BNP",
  lyxor: "Lyxor",
  "franklin templeton": "Franklin",
  pimco: "PIMCO",
  bitwise: "Bitwise",
  hanetf: "HanETF",
};

interface CompanyLogoProps {
  ticker: string;
  exchange: string;
  companyName: string;
  assetClass?: string | null;
  className?: string;
}

const CompanyLogo = ({
  ticker,
  exchange,
  companyName,
  assetClass,
  className,
}: CompanyLogoProps) => {
  const t = useT();
  const [logoError, setLogoError] = useState(false);
  const [logoIndex, setLogoIndex] = useState(0);

  // Reset the error state when the ticker or exchange changes
  useEffect(() => {
    setLogoError(false);
    setLogoIndex(0);
  }, [ticker, exchange, companyName, assetClass]);

  const exchangeFolder = exchange.toUpperCase();

  const providerLogoSources = useMemo(() => {
    const normalizedName = companyName?.toLowerCase() || "";
    const matchedEntry = Object.entries(ETF_PROVIDER_LOGOS).find(([keyword]) =>
      normalizedName.includes(keyword),
    );

    if (!matchedEntry) {
      return [];
    }

    const [, providerLogo] = matchedEntry;
    return [
      `/logos/companies/${ETF_PROVIDER_FOLDER}/${providerLogo}.svg`,
      `/logos/companies/${ETF_PROVIDER_FOLDER}/${providerLogo}.png`,
      `/logos/companies/${ETF_PROVIDER_FOLDER}/${providerLogo}.jpg`,
      `/logos/companies/${ETF_PROVIDER_FOLDER}/${providerLogo}.jpeg`,
    ];
  }, [companyName]);

  const institutionLogoSources = useMemo(() => {
    const normalizedName = companyName?.toLowerCase() || "";
    const matchedEntry = Object.entries(INSTITUTION_LOGOS).find(([keyword]) =>
      normalizedName.includes(keyword),
    );

    if (!matchedEntry) {
      return [];
    }

    const [, institutionLogo] = matchedEntry;
    return [
      `/logos/companies/${INSTITUTION_FOLDER}/${institutionLogo}.svg`,
      `/logos/companies/${INSTITUTION_FOLDER}/${institutionLogo}.png`,
      `/logos/companies/${INSTITUTION_FOLDER}/${institutionLogo}.jpg`,
      `/logos/companies/${INSTITUTION_FOLDER}/${institutionLogo}.jpeg`,
    ];
  }, [companyName]);

  const assetClassLogoSources = useMemo(() => {
    const assetClassLogo = assetClass
      ? ASSET_CLASS_LOGOS[assetClass]
      : undefined;
    if (!assetClassLogo) {
      return [];
    }

    return [
      `/logos/companies/${INSTITUTION_FOLDER}/${assetClassLogo}.svg`,
      `/logos/companies/${INSTITUTION_FOLDER}/${assetClassLogo}.png`,
      `/logos/companies/${INSTITUTION_FOLDER}/${assetClassLogo}.jpg`,
      `/logos/companies/${INSTITUTION_FOLDER}/${assetClassLogo}.jpeg`,
    ];
  }, [assetClass]);

  const logoSources = useMemo(() => {
    const logoName = ticker === "CON" ? "_CON" : ticker;
    const sources = [
      `/logos/companies/${exchangeFolder}/${logoName}.svg`,
      `/logos/companies/${exchangeFolder}/${logoName}.png`,
      `/logos/companies/${exchangeFolder}/${logoName}.jpg`,
      `/logos/companies/${exchangeFolder}/${logoName}.jpeg`,
    ];

    if (providerLogoSources.length > 0) {
      sources.push(...providerLogoSources);
    }

    if (institutionLogoSources.length > 0) {
      sources.push(...institutionLogoSources);
    }

    if (assetClassLogoSources.length > 0) {
      sources.push(...assetClassLogoSources);
    }

    return sources;
  }, [
    exchangeFolder,
    ticker,
    providerLogoSources,
    institutionLogoSources,
    assetClassLogoSources,
  ]);

  const logoSrc = logoSources[logoIndex];
  const isRaster =
    logoSrc?.toLowerCase().endsWith(".png") ||
    logoSrc?.toLowerCase().endsWith(".jpg") ||
    logoSrc?.toLowerCase().endsWith(".jpeg");

  const handleImageError = () => {
    const nextIndex = logoIndex + 1;

    if (nextIndex < logoSources.length) {
      setLogoIndex(nextIndex);
      return;
    }

    setLogoError(true);
  };

  const containerClasses = `${className || "h-10 w-10"} flex items-center justify-center overflow-hidden shrink-0 ${isRaster ? "rounded-lg" : ""} ${logoError ? "bg-muted/30 rounded-lg" : ""}`;

  return (
    <div className={containerClasses}>
      {logoError ? (
        <Building className="h-3/5 w-3/5 text-muted-foreground/60" />
      ) : (
        <img
          src={logoSrc}
          alt={t("holdings:table.logoAlt", { ticker })}
          className="max-h-full max-w-full object-contain"
          onError={handleImageError}
        />
      )}
    </div>
  );
};

export default CompanyLogo;
